import { useEffect, useRef, useState } from 'react';
import styles from './ProductsThirdLevel.module.scss';
import {
    getProductById,
    getProductImage,
    getRelatedProducts,
    getTranslateNameByProductUuid,
} from '../../services/products.service';
import { useParams } from 'react-router-dom';
import { ProductImage } from '../../shared/product-card/models/product-image.model';
import { decodeImageService } from '../../shared/services';
import FixcenterOrderingTable from '../../components/FixcenterOrderingTable/FixcenterOrderingTable';
import FixcenterSupport from '../../components/fixcenter-support/FixcenterSupport';
import { FixcenterProduct } from '../../shared/models/fixcenter-product.model';
import { getFileByUuid, getFileMapByUuids } from '../../shared/services/attachments.service';
import { AttachmentFile } from '../../shared/models/attachment-file.model';
import { determineIfRedirectOrDownload } from '../../shared/services/attachments.service';
import FixcenterNavigation from '../../components/fixcenter-components/fixcenter-navigation/FixcenterNavigation';
import { PRODUCT_THIRD_LEVEL_PATHS } from '../../shared/constants/breadcrumb.constants';
import useBreadcrumbs from '../../hooks/useBreadcrumbs';
import { Language } from '../../shared/models';
import { getLanguageCode, getLanguages } from '../../shared/services/language.service';
import { useTranslation } from '../../i18n/useTranslation';
import { htmlId } from './products-third-level.service';
import StpViewer from '../../stp-viewer/stp-viewer';
import defaultImage from '../../assets/img/Fixcenter-noimage.png';
import { useBetween } from '../../shared/use-between/use-between';
import { StpViewerUseBetween } from '../../stp-viewer/use-between/stp-viewer-use-between';
import globalStyles from '../../global-styles.module.scss';
import { IRelatedProductsListView } from '../../shared/models/related-product.interface';
import SecondLevelProductCard from '../../components/second-level-product-card/SecondLevelProductCard';
import { Category } from '../../components/products-page/components/products-categories/models/Category.model';
import { Utils } from '../../shared/constants/utilities.constants';
import ProductFilterSkeleton from '../../components/product-filters-menu/product-filter-skeleton/product-filter-skeleton';
import ProductViewerSkeleton from './product-viewer-skeleton/product-viewer-skeleton';
import React from 'react';
import { PDF_EXTENSION, PROTO_EXTENSION, RFA_EXTENSION } from '../../shared/constants/file-extension.constants';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

export default function ProductsThirdLevel() {
    const { t } = useTranslation('translation');
    const documentsRef = useRef(null);
    const orderingRef = useRef(null);
    const relatedProductsRef = useRef(null);
    const supportRef = useRef(null);
    const [product, setProduct] = useState<FixcenterProduct>();
    const [productImage, setProductImage] = useState<string>();
    const [products, setProducts] = useState<FixcenterProduct[]>([]);
    const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
    const [fileMap, setFileMap] = useState<Record<string, string>>({});
    const [proto, setProto] = useState<any>();
    const [protoIsLoading, setProtoIsLoading] = useState<boolean>(true);
    const [viewerIsReady, setViewerIsReady] = useState<boolean>(false);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [translatedProductName, setTranslatedProductName] = useState<string>();
    const [viewProjectsKeyValue, setViewProjectsKeyValue] = useState<Record<string, string>>({});
    const { is3dMode, set3dMode } = useBetween(StpViewerUseBetween);
    const [relatedProducts, setRelatedProducts] = useState<IRelatedProductsListView[]>([]);
    const [breadcrumbsTranslationCodes, setBreadcrumbsTranslationCodes] = useState<Record<string, string>>({});
    const [isSkeletonLoader, setIsSkeletonLoader] = useState<boolean>(true);
    const [attemptGetProtoIsComplete, setAttemptGetProtoIsComplete] = useState<boolean>(false);
    const [nonProtoLabel, setNonProtoLabel] = useState<string>('');
    const _nonProtoLabel: string = 'THE_PRODUCT_DOES_NOT_HAVE_3D_MODE';

    const scrollToRef = (ref: any) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
    };

    const { productId, categoryName, categoryId } = useParams();

    const [breadcrumbs, setBreadcrumbs] = useState([...PRODUCT_THIRD_LEVEL_PATHS]);

    useEffect(() => {
        fetchLanguages();
    }, []);

    useEffect(() => {
        if (is3dMode && attemptGetProtoIsComplete) {
            setNonProtoLabel(t(_nonProtoLabel));
        }
    }, [is3dMode]);

    useEffect(() => {
        const container = document.getElementById('AppContainer');
        if (!container) {
            return;
        }
        if (isSkeletonLoader) {
            container.style.overflowY = 'hidden';
            container.scrollTop = 0;
        } else {
            container.style.overflowY = 'auto';
        }
    }, [isSkeletonLoader]);

    useEffect(() => {
        const updatedBreadcrumbs = [...PRODUCT_THIRD_LEVEL_PATHS];
        if (product && categoryName && categoryId) {
            updatedBreadcrumbs[2] = {
                ...updatedBreadcrumbs[2],
                label: categoryName,
                link: `/webshop/category/${categoryName}/${categoryId}`,
            };
            updatedBreadcrumbs[3] = { ...updatedBreadcrumbs[3], label: product?.name };
        }
        setBreadcrumbs(updatedBreadcrumbs);
    }, [product]);

    useEffect(() => {
        if (product && categoryName && categoryId) {
            fetchTranslateNameByCategoryUuid();
        }
    }, [product]);

    useEffect(() => {
        if (product !== null && product !== undefined && languages.length > 0) {
            const currentLanguageCode = getLanguageCode();
            const foundLanguage = languages.find((x) => x.code === currentLanguageCode);
            if (foundLanguage) {
                const translateProductName: string | undefined = product.translation?.translationObjects.find(
                    (x) => x.languageId === foundLanguage.id
                )?.translation;
                setTranslatedProductName(translateProductName);
            }
        }
    }, [product, languages]);

    useEffect(() => {
        if (productId !== null) {
            fetchProduct();
            fetchProductImage();
            fetchRelatedProducts();
        }
    }, [productId]);

    useBreadcrumbs(breadcrumbs);

    const fetchProduct = (): void => {
        if (productId !== undefined) {
            getProductById(productId)
                .then((response: any) => {
                    if (response) {
                        const product = response.data as FixcenterProduct;
                        setProduct(product);
                        fetchFileMap(product.attachmentIds);
                    }
                })
                .catch((error: any) => {
                    console.error('Błąd podczas pobierania produktu:', error);
                });
        }
    };

    const fetchProductImage = () => {
        if (productId !== undefined) {
            getProductImage(productId).then((response: any) => {
                const image = response.data as ProductImage[];
                setProductImage(decodeImageService(image[0]?.content, image[0]?.name) as string);
            });
        }
    };

    const fetchProtoFile = (protoUuid: string) => {
        setProtoIsLoading(true);
        setViewerIsReady(false);
        getFileByUuid(protoUuid as string).then((response) => {
            if (response) {
                const model: AttachmentFile = response.data;
                if (model) {
                    setProto(model.content);
                    setViewerIsReady(true);
                    setProtoIsLoading(false);
                    set3dMode(true);
                    setAttemptGetProtoIsComplete(true);
                }
            } else {
                setProtoIsLoading(false);
            }
        });
    };

    const getFileDocument = (fileDocumentUuid: string) => {
        getFileByUuid(fileDocumentUuid as string).then((response) => {
            if (response) {
                const model: AttachmentFile = response.data;
                if (model) {
                    determineIfRedirectOrDownload(model);
                }
            }
        });
    };

    const fetchFileMap = async (fileUuids: string[]) => {
        if (productId !== undefined) {
            getFileMapByUuids(fileUuids).then((response: any) => {
                if (response) {
                    const _fileMap = response.data as Record<string, string>;
                    let protoUuid: string | null = null;
                    for (const [key, value] of Object.entries(_fileMap)) {
                        if (value != null && value.endsWith(PROTO_EXTENSION)) {
                            protoUuid = key;
                        } else if (value.endsWith(RFA_EXTENSION) || value.endsWith(PDF_EXTENSION)) {
                            setFileMap((prev) => ({
                                ...prev,
                                ...Utils.createRecord(key, value),
                            }));
                        }
                    }
                    if (protoUuid) {
                        fetchProtoFile(protoUuid);
                    } else {
                        set3dMode(false);
                        setAttemptGetProtoIsComplete(true);
                    }
                }
            });
        }
    };

    const fetchLanguages = async () => {
        getLanguages().then((response: any) => {
            if (response) {
                setLanguages(response.data as Language[]);
            }
        });
    };

    const fetchRelatedProducts = async () => {
        if (productId) {
            const languageCode = localStorage.getItem('lang');
            getRelatedProducts(productId, languageCode).then((response: any) => {
                if (response) {
                    setRelatedProducts(response.data as IRelatedProductsListView[]);
                }
            });
        }
    };

    const fetchTranslateNameByCategoryUuid = async () => {
        if (categoryId && categoryName) {
            const languageCode = getLanguageCode();
            getTranslateNameByProductUuid(categoryId, languageCode).then((response: any) => {
                if (response) {
                    const translateName: string | null = response.data;
                    if (translateName) {
                        setBreadcrumbsTranslationCodes((prev) => ({
                            ...prev,
                            ...Utils.createRecord(categoryName, translateName),
                        }));
                    }
                }
            });
        }
    };

    const handleSetProducts = (products: FixcenterProduct[]) => {
        setProducts(products);
    };

    const handleSetViewKeyValueProducts = (viewProjectKeyValue: Record<string, string>) => {
        setViewProjectsKeyValue(viewProjectKeyValue);
        setIsSkeletonLoader(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.navigation}>
                {product?.name && <FixcenterNavigation breadcrumbsTranslationCodes={breadcrumbsTranslationCodes} />}
            </div>
            <div className={styles.content}>
                <div className={styles.product_container}>
                    <h3 className={styles.product_title}>{translatedProductName && translatedProductName}</h3>
                    {!is3dMode ? (
                        <React.Fragment>
                            <React.Fragment>
                                {!productImage ? (
                                    <React.Fragment>
                                        <ProductViewerSkeleton></ProductViewerSkeleton>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <img
                                            src={productImage ? productImage : defaultImage}
                                            alt={t('Product') as string}
                                        />
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            {protoIsLoading ? (
                                <React.Fragment>
                                    <ProductViewerSkeleton label={nonProtoLabel}></ProductViewerSkeleton>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {viewerIsReady && proto ? (
                                        <StpViewer proto={proto}></StpViewer>
                                    ) : (
                                        <ProductViewerSkeleton></ProductViewerSkeleton>
                                    )}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                    <p className={styles.pictures_disclaimer}>*{t('PICTURES_ARE_INDICATIVE')}</p>
                </div>
                {isSkeletonLoader ? (
                    <section className={styles.description_container}>
                        <ProductFilterSkeleton></ProductFilterSkeleton>
                    </section>
                ) : (
                    <div className={styles.description_container}>
                        <button id={htmlId} onClick={() => scrollToRef(orderingRef)}>
                            {t('ADD_TO_CART')}
                        </button>
                        <p onClick={() => scrollToRef(documentsRef)}>{t('DOCUMENTS')}</p>
                        <p onClick={() => scrollToRef(orderingRef)}>{t('ORDERING')}</p>
                        <p onClick={() => scrollToRef(relatedProductsRef)}>{t('RELATED_PRODUCTS')}</p>
                        <p onClick={() => scrollToRef(supportRef)}>{t('SUPPORT')}</p>
                        <div className={styles.schema_values}>
                            {viewProjectsKeyValue &&
                                Object.keys(viewProjectsKeyValue).map((key) => (
                                    <div className={styles.feature_item} key={key}>
                                        <span className={styles.feature_name}>
                                            {key}: <span className={styles.bold}>{viewProjectsKeyValue[key]}</span>
                                        </span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>
            <div ref={documentsRef} className={styles.section_title}>
                <h3>{t('DOCUMENTS')}</h3>
                <div className={styles.pdf_links_container}>
                    {fileMap &&
                        Object.keys(fileMap).map((key) => (
                            <div
                                key={key}
                                onClick={() => {
                                    getFileDocument(key);
                                }}
                                className={styles.pdf_link}
                            >
                                <DownloadIcon fontSize="small" className={styles.pdf_link_icon} />
                                {fileMap[key].toLowerCase().endsWith(PDF_EXTENSION) ? (
                                    <PictureAsPdfIcon fontSize="small" className={styles.pdf_link_icon} />
                                ) : (
                                    <InsertDriveFileOutlinedIcon fontSize="small" className={styles.pdf_link_icon} />
                                )}
                                <span>{fileMap[key]}</span>
                            </div>
                        ))}
                </div>
            </div>
            <div ref={orderingRef} className={styles.section_title}>
                <h3>{t('ORDERING')}</h3>
                <div className={styles.section_content}>
                    <FixcenterOrderingTable
                        handleSetProducts={handleSetProducts}
                        handleSetViewKeyValueProducts={handleSetViewKeyValueProducts}
                        product={product}
                    />
                </div>
            </div>
            <div ref={relatedProductsRef} className={styles.section_title}>
                <h3 className={globalStyles.uppercase}>{t('RELATED_PRODUCTS')}</h3>
                <div className={styles.cards_container}>
                    {relatedProducts.map((relatedProduct, index) => (
                        <div key={index} className={styles.single_product}>
                            <SecondLevelProductCard
                                categoryName={relatedProduct.catalogueName}
                                product={
                                    {
                                        isDirectory: false,
                                        parentProductId: relatedProduct.categoryUuid,
                                        id: relatedProduct.productUuid,
                                        translation: relatedProduct.translateName,
                                        imageIds: relatedProduct.imageIds,
                                        routeCatagoryName: relatedProduct.catalogueName,
                                    } as Category
                                }
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div ref={supportRef} className={styles.section_title}>
                <h3>{t('SUPPORT')}</h3>
                <div className={styles.section_content}>
                    <FixcenterSupport variant={2} />
                </div>
            </div>
        </div>
    );
}
